import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Car, MapPin, Gauge, Calendar, Download, User, Route, Edit2, Trash2, CheckCircle2, Clock, AlertCircle, Plus, AlertTriangle, Shield, FileCheck, Calculator, Loader2, Sparkles, Fuel, FileText } from "lucide-react";
import * as XLSX from "xlsx";
import { HelpTooltip } from "@/components/HelpTooltip";
import { checkAndDeductCredits } from "@/utils/creditSystem";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import { computeRouteKm, useGoogleMaps } from "@/utils/googleMaps";
import MonthlyGeneratorDialog from "@/components/MonthlyGeneratorDialog";
import FuelReceiptsTab from "@/components/FuelReceiptsTab";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function TripSheetsPanel() {
  // Citim user-ul din ambele storage-uri (localStorage cu "Remember me", sessionStorage altfel)
  const getStoredUser = () => {
    const raw = localStorage.getItem('user') || sessionStorage.getItem('user');
    if (!raw) return {};
    try { return JSON.parse(raw); } catch { return {}; }
  };
  const user = getStoredUser();
  const companyId = user.company_id;
  
  const [trips, setTrips] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  
  // Dialog pentru editare
  const [editTrip, setEditTrip] = useState(null);
  const [editStartKm, setEditStartKm] = useState("");
  const [editEndKm, setEditEndKm] = useState("");
  
  // Dialog pentru închidere traseu
  const [closingTrip, setClosingTrip] = useState(null);
  const [closeEndKm, setCloseEndKm] = useState("");
  const [closeEndLocation, setCloseEndLocation] = useState("");
  
  // Dialog pentru înregistrare cursă nouă
  const [openNewTripDialog, setOpenNewTripDialog] = useState(false);
  const [autoKmInfo, setAutoKmInfo] = useState(null);
  const [calculatingKm, setCalculatingKm] = useState(false);
  const { loaded: gmLoaded, error: gmError } = useGoogleMaps();
  const [openMonthlyGen, setOpenMonthlyGen] = useState(false);
  // Dialog pentru export foaie de parcurs lunară oficială (PDF în Documente)
  const [openExportMonthly, setOpenExportMonthly] = useState(false);
  const [exportWorkerId, setExportWorkerId] = useState("");
  const [exportVehicleId, setExportVehicleId] = useState("");
  const [exportingMonthly, setExportingMonthly] = useState(false);
  const [newTrip, setNewTrip] = useState({
    user_id: "",
    vehicle_id: "",
    start_km: "",
    end_km: "",
    date: new Date().toISOString().split('T')[0],
    purpose: "",
    start_location: "",
    end_location: ""
  });
  
  // State pentru tab-ul activ
  const [activeTab, setActiveTab] = useState("trips");
  
  // State pentru editare vehicul (ITP/RCA)
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [expiringDocs, setExpiringDocs] = useState([]);
  
  // Dialog pentru adăugare vehicul nou
  const [openAddVehicleDialog, setOpenAddVehicleDialog] = useState(false);
  const [newVehicle, setNewVehicle] = useState({
    plate: "",
    name: "",
    model_year: "",
    vehicle_type: "Van",
    initial_km: "",
    itp_expiry_date: "",
    rca_expiry_date: "",
    itp_series: "",
    itp_number: ""
  });

  const fetchTrips = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/trip-sheets?year=${year}&month=${month}&company_id=${companyId || ''}`);
      if (!response.ok) throw new Error("Eroare la încărcare");
      const data = await response.json();
      setTrips(data);
    } catch (error) {
      console.error(error);
      toast.error("Eroare la încărcarea foilor de parcurs");
    } finally {
      setLoading(false);
    }
  };

  const fetchVehicles = async () => {
    try {
      const response = await axios.get(`${API}/vehicles`, { params: { company_id: companyId } });
      setVehicles(response.data);
    } catch (error) {
      console.error("Error fetching vehicles:", error);
    }
  };

  const fetchWorkers = async () => {
    try {
      const response = await axios.get(`${API}/users`, { params: { company_id: companyId } });
      setWorkers(response.data.filter(u => u.role === 'worker'));
    } catch (error) {
      console.error("Error fetching workers:", error);
    }
  };

  useEffect(() => {
    fetchTrips();
    fetchVehicles();
    fetchWorkers();
    fetchExpiringDocs();
  }, [month, year]);
  
  const fetchExpiringDocs = async () => {
    try {
      const response = await axios.get(`${API}/vehicles/expiring`, { 
        params: { company_id: companyId, days: 14 } 
      });
      setExpiringDocs(response.data.expiring || []);
    } catch (error) {
      console.error("Error fetching expiring docs:", error);
    }
  };
  
  const handleAddVehicle = async (e) => {
    e.preventDefault();
    if (!newVehicle.plate || !newVehicle.name) {
      toast.error("Numărul și numele vehiculului sunt obligatorii");
      return;
    }
    
    try {
      await axios.post(`${API}/vehicles`, {
        plate: newVehicle.plate.toUpperCase(),
        name: newVehicle.name,
        model_year: newVehicle.model_year ? parseInt(newVehicle.model_year) : null,
        vehicle_type: newVehicle.vehicle_type,
        initial_km: parseFloat(newVehicle.initial_km) || 0,
        itp_expiry_date: newVehicle.itp_expiry_date || null,
        rca_expiry_date: newVehicle.rca_expiry_date || null,
        itp_series: newVehicle.itp_series || null,
        itp_number: newVehicle.itp_number || null
      }, { params: { company_id: companyId } });
      
      toast.success("Vehicul adăugat cu succes!");
      setOpenAddVehicleDialog(false);
      setNewVehicle({
        plate: "", name: "", model_year: "", vehicle_type: "Van", initial_km: "",
        itp_expiry_date: "", rca_expiry_date: "", itp_series: "", itp_number: ""
      });
      fetchVehicles();
      fetchExpiringDocs();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Eroare la adăugare vehicul");
    }
  };
  
  const handleUpdateVehicle = async () => {
    if (!editingVehicle) return;
    
    try {
      await axios.put(`${API}/vehicles/${editingVehicle.id}`, {
        name: editingVehicle.name,
        itp_expiry_date: editingVehicle.itp_expiry_date || null,
        rca_expiry_date: editingVehicle.rca_expiry_date || null,
        itp_series: editingVehicle.itp_series || null,
        itp_number: editingVehicle.itp_number || null,
        itp_confirmed: false,
        rca_confirmed: false,
        itp_notification_count: 0,
        rca_notification_count: 0
      }, { params: { company_id: companyId } });
      
      toast.success("Vehicul actualizat!");
      setEditingVehicle(null);
      fetchVehicles();
      fetchExpiringDocs();
    } catch (error) {
      toast.error("Eroare la actualizare");
    }
  };
  
  const handleDeleteVehicle = async (vehicleId) => {
    if (!confirm("Sigur doriți să ștergeți acest vehicul?")) return;
    
    try {
      await axios.delete(`${API}/vehicles/${vehicleId}`, { params: { company_id: companyId } });
      toast.success("Vehicul șters!");
      fetchVehicles();
    } catch (error) {
      toast.error("Eroare la ștergere");
    }
  };
  
  const handleConfirmRenewal = async (vehicleId, docType) => {
    try {
      await axios.post(`${API}/vehicles/${vehicleId}/confirm-renewal`, null, {
        params: { company_id: companyId, document_type: docType }
      });
      toast.success(`Programare ${docType} confirmată!`);
      fetchExpiringDocs();
      fetchVehicles();
    } catch (error) {
      toast.error("Eroare la confirmare");
    }
  };
  
  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const d = new Date(dateStr);
    return d.toLocaleDateString('ro-RO', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };
  
  const getDaysUntilExpiry = (dateStr) => {
    if (!dateStr) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const expiry = new Date(dateStr);
    const diff = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
    return diff;
  };
  
  const getExpiryBadge = (dateStr, confirmed) => {
    if (!dateStr) return <Badge variant="outline" className="text-slate-500">Nesetat</Badge>;
    
    const days = getDaysUntilExpiry(dateStr);
    if (days < 0) {
      return <Badge className="bg-red-600">EXPIRAT ({Math.abs(days)} zile)</Badge>;
    } else if (days <= 7) {
      return confirmed 
        ? <Badge className="bg-green-600">Programat ✓</Badge>
        : <Badge className="bg-red-500 animate-pulse">Expiră în {days} zile!</Badge>;
    } else if (days <= 30) {
      return <Badge className="bg-amber-500">Expiră în {days} zile</Badge>;
    }
    return <Badge className="bg-green-600">{formatDate(dateStr)}</Badge>;
  };

  const getLastKmForVehicle = (plate) => {
    const vehicle = vehicles.find(v => v.plate === plate);
    return vehicle?.current_km || vehicle?.initial_km || 0;
  };

  const getLastTripForVehicle = (plate) => {
    const vehicleTrips = trips
      .filter(t => t.vehicle_plate === plate && t.status === 'completed')
      .sort((a, b) => new Date(b.date) - new Date(a.date));
    return vehicleTrips[0] || null;
  };

  const handleDeleteTrip = async (tripId) => {
    if (!confirm("Sigur doriți să ștergeți această foaie de parcurs?")) return;
    try {
      await axios.delete(`${API}/trip-sheets/${tripId}`, { params: { company_id: companyId } });
      toast.success("Foaie de parcurs ștearsă!");
      fetchTrips();
    } catch (error) {
      toast.error("Eroare la ștergere");
    }
  };

  const handleEditTrip = (trip) => {
    setEditTrip(trip);
    setEditStartKm(trip.start_km?.toString() || "");
    setEditEndKm(trip.end_km?.toString() || "");
  };

  const handleSaveEdit = async () => {
    if (!editTrip) return;
    try {
      await axios.put(`${API}/trip-sheets/${editTrip.id}`, {
        start_km: parseFloat(editStartKm) || editTrip.start_km,
        end_km: editEndKm ? parseFloat(editEndKm) : null
      }, { params: { company_id: companyId } });
      toast.success("Foaie de parcurs actualizată!");
      setEditTrip(null);
      fetchTrips();
      fetchVehicles();
    } catch (error) {
      toast.error("Eroare la actualizare");
    }
  };

  const handleOpenCloseTrip = (trip) => {
    setClosingTrip(trip);
    const lastKm = getLastKmForVehicle(trip.vehicle_plate);
    setCloseEndKm(lastKm > trip.start_km ? lastKm.toString() : "");
    setCloseEndLocation(trip.site_name || "");
  };

  const handleCloseTrip = async () => {
    if (!closingTrip) return;
    
    const endKm = parseFloat(closeEndKm);
    if (!endKm || endKm <= closingTrip.start_km) {
      toast.error("Kilometrajul final trebuie să fie mai mare decât cel de start!");
      return;
    }
    
    try {
      await axios.put(`${API}/trip-sheets/${closingTrip.id}/complete`, {
        end_km: endKm,
        end_location: closeEndLocation || closingTrip.site_name || ""
      }, { params: { company_id: companyId } });
      
      const distance = endKm - closingTrip.start_km;
      toast.success(
        <div>
          <p className="font-bold">✓ Traseu închis cu succes!</p>
          <p className="text-sm mt-1">Distanță: {distance.toLocaleString()} km</p>
          <p className="text-sm">Km vehicul actualizat: {endKm.toLocaleString()} km</p>
        </div>,
        { duration: 5000 }
      );
      
      setClosingTrip(null);
      setCloseEndKm("");
      setCloseEndLocation("");
      fetchTrips();
      fetchVehicles();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Eroare la închiderea traseului");
    }
  };

  // Înregistrare cursă nouă de către admin
  const handleCreateTrip = async (e) => {
    e.preventDefault();
    
    if (!newTrip.user_id || !newTrip.vehicle_id || !newTrip.start_km) {
      toast.error("Completați toate câmpurile obligatorii!");
      return;
    }
    
    const selectedWorker = workers.find(w => w.id === newTrip.user_id);
    const selectedVehicle = vehicles.find(v => v.id === newTrip.vehicle_id);
    
    if (!selectedWorker || !selectedVehicle) {
      toast.error("Selectați un angajat și un vehicul valid!");
      return;
    }
    
    try {
      const tripData = {
        user_id: newTrip.user_id,
        user_name: selectedWorker.name,
        vehicle_plate: selectedVehicle.plate,
        vehicle_name: selectedVehicle.name,
        start_km: parseFloat(newTrip.start_km),
        end_km: newTrip.end_km ? parseFloat(newTrip.end_km) : null,
        start_location: newTrip.start_location || "",
        end_location: newTrip.end_location || "",
        purpose: newTrip.purpose || "Cursă înregistrată manual",
        date: new Date(newTrip.date).toISOString(),
        status: newTrip.end_km ? "completed" : "in_progress",
        distance_km: newTrip.end_km ? parseFloat(newTrip.end_km) - parseFloat(newTrip.start_km) : 0
      };
      
      await axios.post(`${API}/trip-sheets/manual`, tripData, { params: { company_id: companyId } });
      
      toast.success(
        <div>
          <p className="font-bold">✓ Cursă înregistrată!</p>
          <p className="text-sm mt-1">Șofer: {selectedWorker.name}</p>
          <p className="text-sm">Vehicul: {selectedVehicle.plate}</p>
        </div>,
        { duration: 5000 }
      );
      
      setOpenNewTripDialog(false);
      setNewTrip({
        user_id: "",
        vehicle_id: "",
        start_km: "",
        end_km: "",
        date: new Date().toISOString().split('T')[0],
        purpose: "",
        start_location: "",
        end_location: ""
      });
      setAutoKmInfo(null);
      setCalculatingKm(false);
      fetchTrips();
      fetchVehicles();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Eroare la înregistrarea cursei");
    }
  };

  // Selectare vehicul - pre-completează km
  const handleVehicleSelect = (vehicleId) => {
    const vehicle = vehicles.find(v => v.id === vehicleId);
    setNewTrip({
      ...newTrip,
      vehicle_id: vehicleId,
      start_km: vehicle?.current_km?.toString() || vehicle?.initial_km?.toString() || ""
    });
  };

  const exportToExcel = async () => {
    if (trips.length === 0) {
      toast.error("Nu există date de exportat");
      return;
    }

    // Verifică și deduce creditele
    const creditResult = await checkAndDeductCredits(
      companyId,
      'generate_foi_parcurs',
      null,
      (warning) => toast.warning(warning, { duration: 8000 }),
      (block) => toast.error(block, { duration: 8000 })
    );
    
    if (!creditResult.canProceed) {
      return;
    }
    
    if (creditResult.usedBonus) {
      toast.warning("⚠️ Ați folosit documentul bonus gratuit!", { duration: 5000 });
    }

    const wb = XLSX.utils.book_new();
    
    const mainData = [
      ['FOAIE DE PARCURS', '', '', '', '', '', ''],
      [`Perioada: ${new Date(year, month - 1).toLocaleString('ro-RO', { month: 'long', year: 'numeric' })}`, '', '', '', '', '', ''],
      ['', '', '', '', '', '', ''],
      ['Data', 'Șofer', 'Vehicul', 'Nr. Înmatriculare', 'Km Start', 'Km Final', 'Distanță (km)', 'Destinație', 'Status'],
      ...trips.map(t => [
        new Date(t.date).toLocaleDateString('ro-RO'),
        t.user_name,
        t.vehicle_name,
        t.vehicle_plate,
        t.start_km,
        t.end_km || '-',
        t.distance_km || 0,
        t.site_name || t.end_location || '-',
        t.status === 'completed' ? 'Finalizat' : 'În curs'
      ])
    ];
    
    const totalKm = trips.reduce((sum, t) => sum + (t.distance_km || 0), 0);
    mainData.push(['', '', '', '', '', '', 'TOTAL:', totalKm, '']);
    
    const ws = XLSX.utils.aoa_to_sheet(mainData);
    ws['!cols'] = [
      { wch: 12 }, { wch: 20 }, { wch: 15 }, { wch: 15 }, 
      { wch: 10 }, { wch: 10 }, { wch: 12 }, { wch: 25 }, { wch: 10 }
    ];
    
    XLSX.utils.book_append_sheet(wb, ws, 'Foi de Parcurs');
    
    const monthName = new Date(year, month - 1).toLocaleString('ro-RO', { month: 'long' });
    XLSX.writeFile(wb, `foi_parcurs_${monthName}_${year}.xlsx`);
    toast.success("Export Excel generat!");
  };

  // === Export Foaie de Parcurs Lunară oficială (PDF în Documente) ===
  const handleExportMonthlyPdf = async () => {
    if (!exportWorkerId || !exportVehicleId) {
      toast.error("Selectează șoferul și vehiculul.");
      return;
    }
    const vehicle = vehicles.find((v) => v.id === exportVehicleId);
    if (!vehicle) {
      toast.error("Vehicul invalid.");
      return;
    }
    setExportingMonthly(true);
    try {
      const r = await axios.post(
        `${API}/trip-sheets/export-monthly?company_id=${companyId}`,
        {
          worker_id: exportWorkerId,
          vehicle_plate: vehicle.plate,
          year: Number(year),
          month: Number(month),
        }
      );
      const { html, total_km, trips_count, registration_number } = r.data;
      // Deschide într-o fereastră nouă cu auto-print
      const win = window.open("", "_blank");
      if (win) {
        win.document.open();
        win.document.write(html);
        win.document.close();
        setTimeout(() => { try { win.focus(); win.print(); } catch { /* ignore */ } }, 600);
      } else {
        toast.error("Permite pop-up-urile pentru a vizualiza/printa documentul.");
      }
      toast.success(
        `Foaia lunară #${registration_number} generată — ${trips_count} curse, ${total_km} km. Disponibilă în secțiunea Documente.`,
        { duration: 6000 }
      );
      setOpenExportMonthly(false);
      setExportWorkerId("");
      setExportVehicleId("");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Eroare la export.";
      toast.error(msg);
    } finally {
      setExportingMonthly(false);
    }
  };

  const activeTrips = trips.filter(t => t.status !== 'completed');
  const completedTrips = trips.filter(t => t.status === 'completed');

  const tripsByDate = completedTrips.reduce((acc, trip) => {
    const date = trip.date?.split('T')[0] || 'Unknown';
    if (!acc[date]) acc[date] = [];
    acc[date].push(trip);
    return acc;
  }, {});

  const totalKm = trips.reduce((sum, t) => sum + (t.distance_km || 0), 0);
  const totalTrips = trips.length;

  return (
    <Card className="border-0 shadow-lg">
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2 text-xl" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
              <Car className="w-6 h-6 text-blue-600" />
              Foaie de Parcurs & Flotă Auto
              <HelpTooltip section="foaieParcurs" />
            </CardTitle>
            <CardDescription>Evidența deplasărilor și gestiunea vehiculelor</CardDescription>
          </div>
          
          {/* Alertă documente care expiră */}
          {expiringDocs.length > 0 && (
            <div className="flex items-center gap-2 bg-red-100 border border-red-300 px-3 py-2 rounded-lg animate-pulse">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              <span className="text-red-700 font-medium text-sm">
                {expiringDocs.length} document(e) expiră curând!
              </span>
            </div>
          )}
        </div>
      </CardHeader>
      
      <CardContent>
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="mb-4">
            <TabsTrigger value="trips" className="flex items-center gap-2">
              <Route className="w-4 h-4" />
              Foi de Parcurs
            </TabsTrigger>
            <TabsTrigger value="fleet" className="flex items-center gap-2">
              <Car className="w-4 h-4" />
              Flotă Auto
              {expiringDocs.length > 0 && (
                <Badge className="bg-red-500 ml-1 text-xs">{expiringDocs.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="fuel" className="flex items-center gap-2" data-testid="fuel-tab-trigger">
              <Fuel className="w-4 h-4" />
              Bonuri Motorină
            </TabsTrigger>
          </TabsList>
          
          {/* Tab Foi de Parcurs */}
          <TabsContent value="trips">
            <div className="flex gap-2 items-center flex-wrap mb-4">
              {/* Buton Înregistrează Cursă */}
              <Dialog open={openNewTripDialog} onOpenChange={setOpenNewTripDialog}>
                <DialogTrigger asChild>
                  <Button className="bg-green-600 hover:bg-green-700" data-testid="register-trip-button">
                    <Plus className="w-4 h-4 mr-2" />
                    Înregistrează Cursă
                  </Button>
                </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2 text-green-700">
                    <Car className="w-5 h-5" />
                    Înregistrare Cursă Manuală
                  </DialogTitle>
                  <DialogDescription>
                    Adăugați o cursă uitată de angajat sau înregistrați manual o deplasare.
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleCreateTrip} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Angajat *</Label>
                      <Select value={newTrip.user_id} onValueChange={(v) => setNewTrip({...newTrip, user_id: v})}>
                        <SelectTrigger data-testid="trip-worker-select">
                          <SelectValue placeholder="Selectează" />
                        </SelectTrigger>
                        <SelectContent>
                          {workers.map(w => (
                            <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Vehicul *</Label>
                      <Select value={newTrip.vehicle_id} onValueChange={handleVehicleSelect}>
                        <SelectTrigger data-testid="trip-vehicle-select">
                          <SelectValue placeholder="Selectează" />
                        </SelectTrigger>
                        <SelectContent>
                          {vehicles.map(v => (
                            <SelectItem key={v.id} value={v.id}>
                              {v.plate} - {v.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div>
                    <Label>Data Cursei *</Label>
                    <Input
                      type="date"
                      value={newTrip.date}
                      onChange={(e) => setNewTrip({...newTrip, date: e.target.value})}
                      data-testid="trip-date-input"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Km Start *</Label>
                      <Input
                        type="number"
                        value={newTrip.start_km}
                        onChange={(e) => setNewTrip({...newTrip, start_km: e.target.value})}
                        placeholder="Km la plecare"
                        data-testid="trip-start-km-input"
                      />
                    </div>
                    <div>
                      <Label>Km Final (opțional)</Label>
                      <Input
                        type="number"
                        value={newTrip.end_km}
                        onChange={(e) => setNewTrip({...newTrip, end_km: e.target.value})}
                        placeholder="Lasă gol dacă e în curs"
                        data-testid="trip-end-km-input"
                      />
                    </div>
                  </div>
                  
                  {newTrip.start_km && newTrip.end_km && (
                    <div className="p-2 bg-blue-50 rounded border border-blue-200 text-sm text-blue-700">
                      Distanță: <strong>{(parseFloat(newTrip.end_km) - parseFloat(newTrip.start_km)).toLocaleString()} km</strong>
                    </div>
                  )}
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label>Locație Plecare</Label>
                      <AddressAutocomplete
                        value={newTrip.start_location}
                        onChange={(v) => setNewTrip({ ...newTrip, start_location: v })}
                        placeholder="Ex: Sediu București, Str. ..."
                        testId="trip-start-location"
                      />
                    </div>
                    <div>
                      <Label>Destinație</Label>
                      <AddressAutocomplete
                        value={newTrip.end_location}
                        onChange={(v) => setNewTrip({ ...newTrip, end_location: v })}
                        placeholder="Ex: Șantier Pipera, ..."
                        testId="trip-end-location"
                      />
                    </div>
                  </div>

                  {gmError && (
                    <div className="p-2.5 rounded-md bg-amber-50 border border-amber-200 text-xs text-amber-800 flex items-start gap-2" data-testid="gm-disabled-notice">
                      <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <div>
                        <strong>Google Maps indisponibil.</strong> Poți tasta manual adresele. Pentru autocomplete și calcul automat de km, activează în Google Cloud Console: <em>Maps JavaScript API</em>, <em>Places API</em> și <em>Distance Matrix API</em> pentru cheia ta.
                      </div>
                    </div>
                  )}

                  {/* Buton calcul automat KM via Google Maps Distance Matrix */}
                  {newTrip.start_location && newTrip.end_location && newTrip.start_km && !gmError && (
                    <div className="flex items-center justify-between gap-3 p-3 rounded-lg bg-teal-50 border border-teal-200">
                      <div className="text-xs text-teal-700">
                        <span className="font-semibold">Distanță automată:</span>{" "}
                        {autoKmInfo
                          ? `${autoKmInfo.distanceText} · ${autoKmInfo.durationText} cu mașina`
                          : "calculează km între cele 2 adrese cu Google Maps"}
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={calculatingKm || !gmLoaded}
                        onClick={async () => {
                          setCalculatingKm(true);
                          try {
                            const r = await computeRouteKm(newTrip.start_location, newTrip.end_location);
                            setAutoKmInfo(r);
                            const km = r.distanceMeters / 1000;
                            const startKm = parseFloat(newTrip.start_km) || 0;
                            const endKm = (startKm + km).toFixed(1);
                            setNewTrip((p) => ({ ...p, end_km: endKm }));
                            toast.success(`Distanță calculată: ${r.distanceText}`, {
                              description: `KM stop completat automat: ${endKm}`,
                            });
                          } catch (err) {
                            console.error(err);
                            const msg = String(err?.message || "");
                            if (msg.includes("REQUEST_DENIED") || msg.includes("not ready")) {
                              toast.error("Distance Matrix API nu este activat", {
                                description: "Activează 'Distance Matrix API' în Google Cloud Console pentru cheia ta."
                              });
                            } else {
                              toast.error("Nu am putut calcula distanța. Verifică adresele.");
                            }
                          } finally {
                            setCalculatingKm(false);
                          }
                        }}
                        className="border-teal-400 text-teal-700 hover:bg-teal-100 flex-shrink-0"
                        data-testid="auto-km-btn"
                      >
                        {calculatingKm ? (
                          <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                        ) : (
                          <Calculator className="w-3.5 h-3.5 mr-1" />
                        )}
                        Calculează KM
                      </Button>
                    </div>
                  )}
                  
                  <div>
                    <Label>Scopul Deplasării</Label>
                    <Input
                      value={newTrip.purpose}
                      onChange={(e) => setNewTrip({...newTrip, purpose: e.target.value})}
                      placeholder="Ex: Transport materiale"
                    />
                  </div>
                  
                  <div className="flex gap-2 pt-2">
                    <Button type="submit" className="flex-1 bg-green-600 hover:bg-green-700" data-testid="submit-trip-button">
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      Înregistrează Cursa
                    </Button>
                    <Button type="button" variant="outline" onClick={() => setOpenNewTripDialog(false)} className="flex-1">
                      Anulează
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>

            {/* Buton Generare automată lunară */}
            <Button
              type="button"
              onClick={() => setOpenMonthlyGen(true)}
              className="bg-gradient-to-r from-violet-500 to-fuchsia-500 hover:from-violet-600 hover:to-fuchsia-600 text-white"
              data-testid="monthly-generator-button"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Generare automată (lună)
            </Button>
            
            <Select value={month.toString()} onValueChange={(v) => setMonth(parseInt(v))}>
              <SelectTrigger className="w-32">
                <SelectValue placeholder="Luna" />
              </SelectTrigger>
              <SelectContent>
                {[...Array(12)].map((_, i) => (
                  <SelectItem key={i + 1} value={(i + 1).toString()}>
                    {new Date(2000, i).toLocaleString('ro-RO', { month: 'long' })}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={year.toString()} onValueChange={(v) => setYear(parseInt(v))}>
              <SelectTrigger className="w-24">
                <SelectValue placeholder="An" />
              </SelectTrigger>
              <SelectContent>
                {[2024, 2025, 2026].map(y => (
                  <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Button onClick={exportToExcel} variant="outline" className="gap-2">
              <Download className="w-4 h-4" />
              Excel
            </Button>

            <Button
              onClick={() => setOpenExportMonthly(true)}
              variant="outline"
              className="gap-2 border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
              data-testid="export-monthly-pdf-button"
              title="Generează Foaia de Parcurs lunară oficială (PDF) și o salvează în Documente"
            >
              <FileText className="w-4 h-4" />
              Exportă lunar (PDF)
            </Button>
          </div>
        
        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <div className="flex items-center gap-2 text-blue-700">
              <Route className="w-5 h-5" />
              <span className="font-semibold">Total Km</span>
            </div>
            <p className="text-2xl font-bold text-blue-800 mt-1">{totalKm.toLocaleString()} km</p>
          </div>
          
          <div className="bg-green-50 p-4 rounded-lg border border-green-200">
            <div className="flex items-center gap-2 text-green-700">
              <Car className="w-5 h-5" />
              <span className="font-semibold">Nr. Curse</span>
            </div>
            <p className="text-2xl font-bold text-green-800 mt-1">{totalTrips}</p>
          </div>
          
          <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
            <div className="flex items-center gap-2 text-purple-700">
              <Gauge className="w-5 h-5" />
              <span className="font-semibold">Media/Cursă</span>
            </div>
            <p className="text-2xl font-bold text-purple-800 mt-1">
              {totalTrips > 0 ? Math.round(totalKm / totalTrips) : 0} km
            </p>
          </div>
          
          <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
            <div className="flex items-center gap-2 text-orange-700">
              <Clock className="w-5 h-5" />
              <span className="font-semibold">În Curs</span>
            </div>
            <p className="text-2xl font-bold text-orange-800 mt-1">{activeTrips.length}</p>
          </div>
        </div>

        {/* Active Trips Section */}
        {activeTrips.length > 0 && (
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-3 flex items-center gap-2 text-orange-700">
              <AlertCircle className="w-5 h-5" />
              Trasee În Curs ({activeTrips.length})
            </h3>
            <div className="space-y-3">
              {activeTrips.map((trip) => {
                const lastTrip = getLastTripForVehicle(trip.vehicle_plate);
                const vehicleCurrentKm = getLastKmForVehicle(trip.vehicle_plate);
                
                return (
                  <div 
                    key={trip.id} 
                    className="p-4 bg-gradient-to-r from-orange-50 to-yellow-50 rounded-lg border-2 border-orange-300 shadow-md"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-orange-500 to-yellow-500 flex items-center justify-center animate-pulse">
                          <Car className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-bold text-lg">{trip.user_name}</p>
                            <Badge className="bg-orange-500 text-white">{trip.vehicle_plate}</Badge>
                          </div>
                          <p className="text-sm text-slate-600">{trip.vehicle_name}</p>
                          <p className="text-xs text-slate-500">
                            Plecat: {new Date(trip.date).toLocaleString('ro-RO', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                      </div>
                      
                      <div className="text-right">
                        <Badge className="bg-yellow-500 text-black mb-2">
                          <Clock className="w-3 h-3 mr-1" />În curs
                        </Badge>
                        <p className="text-sm text-slate-600">Km start: <strong>{trip.start_km?.toLocaleString()}</strong></p>
                      </div>
                    </div>
                    
                    <div className="mt-3 flex items-center gap-2 text-sm text-slate-600">
                      <MapPin className="w-4 h-4" />
                      <span>Destinație: <strong>{trip.site_name || trip.purpose || '-'}</strong></span>
                    </div>
                    
                    {lastTrip && (
                      <div className="mt-2 p-2 bg-white/50 rounded text-xs text-slate-500">
                        <strong>Tura anterioară:</strong> {lastTrip.user_name} - {lastTrip.distance_km?.toLocaleString() || 0} km
                      </div>
                    )}
                    
                    <div className="mt-3 flex gap-2">
                      <Button onClick={() => handleOpenCloseTrip(trip)} className="flex-1 bg-green-600 hover:bg-green-700">
                        <CheckCircle2 className="w-4 h-4 mr-2" />Închide Traseul
                      </Button>
                      <Button variant="outline" onClick={() => handleEditTrip(trip)} className="text-blue-600">
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button variant="outline" onClick={() => handleDeleteTrip(trip.id)} className="text-red-600 hover:bg-red-50">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Completed Trips */}
        <h3 className="text-lg font-semibold mb-3 flex items-center gap-2 text-green-700">
          <CheckCircle2 className="w-5 h-5" />
          Trasee Finalizate
        </h3>
        
        {loading ? (
          <div className="text-center py-8 text-slate-500">Se încarcă...</div>
        ) : completedTrips.length === 0 ? (
          <div className="text-center py-8 text-slate-500">Nu există trasee finalizate pentru această perioadă.</div>
        ) : (
          <ScrollArea className="h-[350px]">
            <div className="space-y-4">
              {Object.entries(tripsByDate).sort((a, b) => b[0].localeCompare(a[0])).map(([date, dayTrips]) => (
                <div key={date} className="border rounded-lg overflow-hidden">
                  <div className="bg-slate-100 px-4 py-2 font-semibold text-sm text-slate-700">
                    {new Date(date).toLocaleDateString('ro-RO', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                  </div>
                  <div className="divide-y">
                    {dayTrips.map((trip) => (
                      <div key={trip.id} className="p-4 hover:bg-slate-50 transition-colors">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-green-500 flex items-center justify-center">
                              <Car className="w-5 h-5 text-white" />
                            </div>
                            <div>
                              <p className="font-semibold">{trip.user_name}</p>
                              <p className="text-sm text-slate-600">{trip.vehicle_plate} • {trip.vehicle_name}</p>
                            </div>
                          </div>
                          <div className="text-right flex items-start gap-2">
                            <div>
                              <p className="text-lg font-bold text-green-600">{trip.distance_km || 0} km</p>
                              <p className="text-xs text-slate-500">{trip.start_km} → {trip.end_km}</p>
                            </div>
                            <div className="flex gap-1">
                              <Button variant="ghost" size="sm" onClick={() => handleEditTrip(trip)} className="text-blue-500">
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => handleDeleteTrip(trip.id)} className="text-red-500">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        )}
      
      {/* Dialog Închidere Traseu */}
      <Dialog open={!!closingTrip} onOpenChange={(open) => !open && setClosingTrip(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-700">
              <CheckCircle2 className="w-5 h-5" />Închide Traseul
            </DialogTitle>
          </DialogHeader>
          {closingTrip && (
            <div className="space-y-4">
              <div className="p-3 bg-slate-50 rounded-lg text-sm">
                <p><strong>Șofer:</strong> {closingTrip.user_name}</p>
                <p><strong>Vehicul:</strong> {closingTrip.vehicle_plate}</p>
                <p><strong>Km start:</strong> {closingTrip.start_km?.toLocaleString()}</p>
              </div>
              <div>
                <Label>Kilometraj Final *</Label>
                <Input type="number" value={closeEndKm} onChange={(e) => setCloseEndKm(e.target.value)} min={closingTrip.start_km + 1} />
              </div>
              <div>
                <Label>Locație Sosire</Label>
                <AddressAutocomplete
                  value={closeEndLocation}
                  onChange={setCloseEndLocation}
                  testId="close-end-location"
                  placeholder="Adresa de sosire"
                />
              </div>
              {/* Calcul automat KM dacă avem ambele adrese */}
              {closingTrip.start_location && closeEndLocation && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="border-teal-400 text-teal-700 hover:bg-teal-50"
                  data-testid="close-trip-auto-km-btn"
                  onClick={async () => {
                    try {
                      const r = await computeRouteKm(closingTrip.start_location, closeEndLocation);
                      const km = r.distanceMeters / 1000;
                      const newEndKm = (closingTrip.start_km + km).toFixed(1);
                      setCloseEndKm(newEndKm);
                      toast.success(`Distanță: ${r.distanceText}`, { description: `KM final: ${newEndKm}` });
                    } catch (err) {
                      const msg = String(err?.message || "");
                      if (msg.includes("REQUEST_DENIED") || msg.includes("not ready")) {
                        toast.error("Distance Matrix API nu este activat", {
                          description: "Activează 'Distance Matrix API' în Google Cloud Console.",
                        });
                      } else {
                        toast.error("Nu am putut calcula distanța");
                      }
                    }
                  }}
                >
                  <Calculator className="w-3.5 h-3.5 mr-1" />
                  Calculează KM automat
                </Button>
              )}
              {closeEndKm && parseFloat(closeEndKm) > closingTrip.start_km && (
                <div className="p-2 bg-green-50 rounded border border-green-200 text-sm text-green-700">
                  Distanță: <strong>{(parseFloat(closeEndKm) - closingTrip.start_km).toLocaleString()} km</strong>
                </div>
              )}
              <div className="flex gap-2">
                <Button onClick={handleCloseTrip} className="flex-1 bg-green-600 hover:bg-green-700">Închide Traseul</Button>
                <Button variant="outline" onClick={() => setClosingTrip(null)} className="flex-1">Anulează</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Edit Trip Dialog */}
      <Dialog open={!!editTrip} onOpenChange={(open) => !open && setEditTrip(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Edit2 className="w-5 h-5 text-blue-600" />Editează Foaia de Parcurs</DialogTitle>
          </DialogHeader>
          {editTrip && (
            <div className="space-y-4">
              <div className="p-3 bg-slate-50 rounded-lg text-sm">
                <p><strong>Șofer:</strong> {editTrip.user_name}</p>
                <p><strong>Vehicul:</strong> {editTrip.vehicle_plate}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Km Start</Label>
                  <Input type="number" value={editStartKm} onChange={(e) => setEditStartKm(e.target.value)} />
                </div>
                <div>
                  <Label>Km Final</Label>
                  <Input type="number" value={editEndKm} onChange={(e) => setEditEndKm(e.target.value)} />
                </div>
              </div>
              <div className="flex gap-2">
                <Button onClick={handleSaveEdit} className="flex-1 bg-blue-600 hover:bg-blue-700">Salvează</Button>
                <Button variant="outline" onClick={() => setEditTrip(null)} className="flex-1">Anulează</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
          </TabsContent>
          
          {/* Tab Flotă Auto */}
          <TabsContent value="fleet">
            <div className="space-y-6">
              {/* Header cu buton adăugare */}
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Car className="w-5 h-5 text-blue-600" />
                  Vehicule Companie ({vehicles.length})
                </h3>
                <Dialog open={openAddVehicleDialog} onOpenChange={setOpenAddVehicleDialog}>
                  <DialogTrigger asChild>
                    <Button className="bg-blue-600 hover:bg-blue-700">
                      <Plus className="w-4 h-4 mr-2" />
                      Adaugă Vehicul
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-lg">
                    <DialogHeader>
                      <DialogTitle className="flex items-center gap-2 text-blue-700">
                        <Car className="w-5 h-5" />
                        Adaugă Vehicul Nou
                      </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleAddVehicle} className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Nr. Înmatriculare *</Label>
                          <Input
                            value={newVehicle.plate}
                            onChange={(e) => setNewVehicle({...newVehicle, plate: e.target.value.toUpperCase()})}
                            placeholder="B 123 ABC"
                            data-testid="vehicle-plate-input"
                          />
                        </div>
                        <div>
                          <Label>Nume/Model *</Label>
                          <Input
                            value={newVehicle.name}
                            onChange={(e) => setNewVehicle({...newVehicle, name: e.target.value})}
                            placeholder="Dacia Dokker"
                          />
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <Label>An Fabricație</Label>
                          <Input
                            type="number"
                            value={newVehicle.model_year}
                            onChange={(e) => setNewVehicle({...newVehicle, model_year: e.target.value})}
                            placeholder="2020"
                          />
                        </div>
                        <div>
                          <Label>Tip Vehicul</Label>
                          <Select value={newVehicle.vehicle_type} onValueChange={(v) => setNewVehicle({...newVehicle, vehicle_type: v})}>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Van">Van/Utilitar</SelectItem>
                              <SelectItem value="Car">Autoturism</SelectItem>
                              <SelectItem value="Truck">Camion</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Km Inițiali</Label>
                          <Input
                            type="number"
                            value={newVehicle.initial_km}
                            onChange={(e) => setNewVehicle({...newVehicle, initial_km: e.target.value})}
                            placeholder="0"
                          />
                        </div>
                      </div>
                      
                      <div className="border-t pt-4">
                        <h4 className="font-medium mb-3 flex items-center gap-2 text-amber-700">
                          <FileCheck className="w-4 h-4" />
                          Documente Auto
                        </h4>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label className="flex items-center gap-2">
                              <Shield className="w-4 h-4 text-blue-600" />
                              Data Expirare ITP
                            </Label>
                            <Input
                              type="date"
                              value={newVehicle.itp_expiry_date}
                              onChange={(e) => setNewVehicle({...newVehicle, itp_expiry_date: e.target.value})}
                            />
                          </div>
                          <div>
                            <Label className="flex items-center gap-2">
                              <Shield className="w-4 h-4 text-green-600" />
                              Data Expirare RCA
                            </Label>
                            <Input
                              type="date"
                              value={newVehicle.rca_expiry_date}
                              onChange={(e) => setNewVehicle({...newVehicle, rca_expiry_date: e.target.value})}
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4 mt-3">
                          <div>
                            <Label>Serie ITP</Label>
                            <Input
                              value={newVehicle.itp_series}
                              onChange={(e) => setNewVehicle({...newVehicle, itp_series: e.target.value})}
                              placeholder="Serie certificat"
                            />
                          </div>
                          <div>
                            <Label>Număr ITP</Label>
                            <Input
                              value={newVehicle.itp_number}
                              onChange={(e) => setNewVehicle({...newVehicle, itp_number: e.target.value})}
                              placeholder="Număr certificat"
                            />
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex gap-2 pt-2">
                        <Button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700">
                          <Plus className="w-4 h-4 mr-2" />
                          Adaugă Vehicul
                        </Button>
                        <Button type="button" variant="outline" onClick={() => setOpenAddVehicleDialog(false)} className="flex-1">
                          Anulează
                        </Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
              
              {/* Alertă documente care expiră */}
              {expiringDocs.length > 0 && (
                <div className="bg-red-50 border-2 border-red-300 rounded-lg p-4">
                  <h4 className="font-semibold text-red-700 mb-3 flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5" />
                    Documente care expiră ({expiringDocs.length})
                  </h4>
                  <div className="space-y-2">
                    {expiringDocs.map((doc, idx) => (
                      <div key={`${doc.document_type}-${doc.plate}-${idx}`} className="flex items-center justify-between bg-white p-3 rounded border border-red-200">
                        <div className="flex items-center gap-3">
                          <Badge className={doc.document_type === 'ITP' ? 'bg-blue-600' : 'bg-green-600'}>
                            {doc.document_type}
                          </Badge>
                          <div>
                            <span className="font-medium">{doc.plate}</span>
                            <span className="text-slate-500 mx-2">-</span>
                            <span>{doc.name}</span>
                          </div>
                          <Badge className={doc.days_left <= 3 ? 'bg-red-600' : 'bg-amber-500'}>
                            {doc.days_left <= 0 ? 'EXPIRAT!' : `Expiră în ${doc.days_left} zile`}
                          </Badge>
                        </div>
                        <Button 
                          size="sm" 
                          className="bg-green-600 hover:bg-green-700"
                          onClick={() => handleConfirmRenewal(doc.vehicle_id, doc.document_type)}
                        >
                          <CheckCircle2 className="w-4 h-4 mr-1" />
                          Confirm Programare
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Lista vehicule */}
              <div className="grid gap-4">
                {vehicles.length === 0 ? (
                  <div className="text-center py-12 bg-slate-50 rounded-lg">
                    <Car className="w-12 h-12 mx-auto text-slate-400 mb-2" />
                    <p className="text-slate-500">Nu aveți vehicule adăugate</p>
                  </div>
                ) : (
                  vehicles.map((v) => (
                    <div key={v.id} className="bg-white border rounded-lg p-4 hover:shadow-md transition-shadow">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-14 h-14 rounded-lg bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-white">
                            <Car className="w-7 h-7" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-bold text-lg">{v.plate}</span>
                              <Badge variant="outline">{v.vehicle_type}</Badge>
                              {v.model_year && <span className="text-slate-500">({v.model_year})</span>}
                            </div>
                            <p className="text-slate-600">{v.name}</p>
                            <p className="text-sm text-slate-500">
                              <Gauge className="w-4 h-4 inline mr-1" />
                              {(v.current_km || v.initial_km || 0).toLocaleString()} km
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => setEditingVehicle({...v})}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            className="text-red-600 hover:bg-red-50"
                            onClick={() => handleDeleteVehicle(v.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      
                      {/* ITP și RCA */}
                      <div className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t">
                        <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                          <div className="flex items-center gap-2">
                            <Shield className="w-5 h-5 text-blue-600" />
                            <span className="font-medium">ITP</span>
                          </div>
                          {getExpiryBadge(v.itp_expiry_date, v.itp_confirmed)}
                        </div>
                        <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                          <div className="flex items-center gap-2">
                            <Shield className="w-5 h-5 text-green-600" />
                            <span className="font-medium">RCA</span>
                          </div>
                          {getExpiryBadge(v.rca_expiry_date, v.rca_confirmed)}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </TabsContent>

          {/* Tab Bonuri Motorină */}
          <TabsContent value="fuel">
            <FuelReceiptsTab companyId={companyId} workers={workers} vehicles={vehicles} />
          </TabsContent>
        </Tabs>
      </CardContent>
      
      {/* Dialog Editare Vehicul */}
      <Dialog open={!!editingVehicle} onOpenChange={(open) => !open && setEditingVehicle(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-700">
              <Edit2 className="w-5 h-5" />
              Editează Vehicul - {editingVehicle?.plate}
            </DialogTitle>
          </DialogHeader>
          {editingVehicle && (
            <div className="space-y-4">
              <div>
                <Label>Nume/Model</Label>
                <Input
                  value={editingVehicle.name}
                  onChange={(e) => setEditingVehicle({...editingVehicle, name: e.target.value})}
                />
              </div>
              
              <div className="border-t pt-4">
                <h4 className="font-medium mb-3 flex items-center gap-2 text-amber-700">
                  <FileCheck className="w-4 h-4" />
                  Documente Auto
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="flex items-center gap-2">
                      <Shield className="w-4 h-4 text-blue-600" />
                      Data Expirare ITP
                    </Label>
                    <Input
                      type="date"
                      value={editingVehicle.itp_expiry_date || ''}
                      onChange={(e) => setEditingVehicle({...editingVehicle, itp_expiry_date: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label className="flex items-center gap-2">
                      <Shield className="w-4 h-4 text-green-600" />
                      Data Expirare RCA
                    </Label>
                    <Input
                      type="date"
                      value={editingVehicle.rca_expiry_date || ''}
                      onChange={(e) => setEditingVehicle({...editingVehicle, rca_expiry_date: e.target.value})}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 mt-3">
                  <div>
                    <Label>Serie ITP</Label>
                    <Input
                      value={editingVehicle.itp_series || ''}
                      onChange={(e) => setEditingVehicle({...editingVehicle, itp_series: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Număr ITP</Label>
                    <Input
                      value={editingVehicle.itp_number || ''}
                      onChange={(e) => setEditingVehicle({...editingVehicle, itp_number: e.target.value})}
                    />
                  </div>
                </div>
              </div>
              
              <div className="flex gap-2 pt-2">
                <Button onClick={handleUpdateVehicle} className="flex-1 bg-blue-600 hover:bg-blue-700">
                  Salvează Modificări
                </Button>
                <Button variant="outline" onClick={() => setEditingVehicle(null)} className="flex-1">
                  Anulează
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Monthly auto-generator */}
      <MonthlyGeneratorDialog
        open={openMonthlyGen}
        onClose={() => setOpenMonthlyGen(false)}
        companyId={companyId}
        workers={workers}
        vehicles={vehicles}
        onSaved={fetchTrips}
      />

      {/* Export Foaie de Parcurs Lunară (PDF oficial în Documente) */}
      <Dialog open={openExportMonthly} onOpenChange={(o) => !exportingMonthly && setOpenExportMonthly(o)}>
        <DialogContent className="max-w-md" data-testid="export-monthly-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-700">
              <FileText className="w-5 h-5" />
              Exportă Foaie de Parcurs Lunară
            </DialogTitle>
            <DialogDescription>
              Generează PDF-ul oficial cu antet firmă, tabel detaliat, total km și locuri de semnătură.
              Documentul se salvează automat în secțiunea <strong>Documente</strong>.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Șofer *</Label>
              <Select value={exportWorkerId} onValueChange={setExportWorkerId}>
                <SelectTrigger data-testid="export-monthly-worker"><SelectValue placeholder="Alege șofer" /></SelectTrigger>
                <SelectContent>
                  {workers.map((w) => (
                    <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Vehicul *</Label>
              <Select value={exportVehicleId} onValueChange={setExportVehicleId}>
                <SelectTrigger data-testid="export-monthly-vehicle"><SelectValue placeholder="Alege vehicul" /></SelectTrigger>
                <SelectContent>
                  {vehicles.map((v) => (
                    <SelectItem key={v.id} value={v.id}>{v.plate} — {v.name || v.brand || ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded p-2 flex items-center gap-2">
              <Calendar className="w-3.5 h-3.5 text-slate-500" />
              Perioadă: <strong>{new Date(year, month - 1).toLocaleString('ro-RO', { month: 'long', year: 'numeric' })}</strong>
              <span className="text-slate-400">(schimbă din selectoarele de lună/an din pagină)</span>
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button variant="outline" onClick={() => setOpenExportMonthly(false)} disabled={exportingMonthly}>
                Anulează
              </Button>
              <Button
                onClick={handleExportMonthlyPdf}
                disabled={exportingMonthly || !exportWorkerId || !exportVehicleId}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="export-monthly-confirm-btn"
              >
                {exportingMonthly ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Se generează...</>
                ) : (
                  <><FileText className="w-4 h-4 mr-2" />Generează & Salvează</>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
